import { useEffect, useRef, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CircleNotch, Receipt, Printer, CaretDown, CaretUp, Trash, SortAscending, Check } from "@phosphor-icons/react";
import { ListHeader, ListToolbar, ToolbarButton, FilterPanel, FilterSelect, FilterDate } from "@/components/ListPage";
import { ReservationDrawer, STATUS_LABELS as CAL_STATUS_LABELS, SOURCE_LABELS as CAL_SOURCE_LABELS, reservationColor } from "./CalendarPage";
import { useProperty } from "@/contexts/PropertyContext";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams } from "react-router-dom";

// Local display map so the badge styling matches the previous table look.
const STATUS_BADGE = {
  confirmed: { bg: "bg-primary/10", text: "text-primary" },
  checked_in: { bg: "bg-success/10", text: "text-success" },
  checked_out: { bg: "bg-muted", text: "text-muted-foreground" },
  tentative: { bg: "bg-warning/10", text: "text-warning" },
  cancelled: { bg: "bg-destructive/10", text: "text-destructive" },
  no_show: { bg: "bg-destructive/10", text: "text-destructive" },
};

const SORT_OPTIONS = [
  { value: "created_at", label: "Datum unosa" },
  { value: "check_in", label: "Datum dolaska" },
  { value: "check_out", label: "Datum odlaska" },
  { value: "total_amount", label: "Iznos" },
  { value: "nights", label: "Broj noći" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const parts = iso.split(/[T\-]/);
  if (parts.length >= 3) return `${parts[2].slice(0, 2)}.${parts[1]}.${parts[0]}`;
  return iso;
};
const fmtShortDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}.${m}.${y.slice(2)}`;
};
const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function ReservationsPage() {
  const { tenant } = useAuth();
  const { activePropertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter state
  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    arrivals_today: false,
    departures_today: false,
    status: "",
    unit_ids: [],
    property_ids: [],
    source: [],
    min_revenue: "",
    max_revenue: "",
    min_nights: "",
    max_nights: "",
    country: "",
    q: "",
  });
  const [showFilters, setShowFilters] = useState(() => window.innerWidth >= 768);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const [drawer, setDrawer] = useState(null); // { reservation }
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  const toggleSel = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = (all) => setSelected(all ? new Set(items.map((r) => r.id)) : new Set());

  const deleteReservation = async (id) => {
    if (!window.confirm("Obrisati ovu rezervaciju? Ova akcija se ne može poništiti.")) return;
    try {
      await api.delete(`/reservations/${id}`);
      setItems((prev) => prev.filter((r) => r.id !== id));
      setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
      toast.success("Rezervacija obrisana");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Greška pri brisanju");
    }
  };

  const deleteSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Obrisati ${ids.length} odabranih rezervacija? Ova akcija se ne može poništiti.`)) return;
    let ok = 0, fail = 0;
    await Promise.all(ids.map(async (id) => {
      try { await api.delete(`/reservations/${id}`); ok += 1; } catch { fail += 1; }
    }));
    setItems((prev) => prev.filter((r) => !selected.has(r.id)));
    setSelected(new Set());
    if (fail === 0) toast.success(`Obrisano ${ok} rezervacija`);
    else toast.error(`Obrisano ${ok}, neuspješno ${fail}`);
  };

  const loadReservations = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.date_from && filters.date_to) { params.set("start", filters.date_from); params.set("end", filters.date_to); }
    if (filters.arrivals_today) params.set("arrivals_on", todayIso());
    if (filters.departures_today) params.set("departures_on", todayIso());
    if (filters.status) params.set("status_filter", filters.status);
    // Global property switcher scopes the query when active.
    if (activePropertyId) params.set("property_id", activePropertyId);
    else if (filters.property_ids.length === 1) params.set("property_id", filters.property_ids[0]);
    if (filters.unit_ids.length === 1) params.set("unit_id", filters.unit_ids[0]);
    if (filters.source.length) params.set("source", filters.source.join(","));
    if (filters.min_revenue !== "") params.set("min_revenue", filters.min_revenue);
    if (filters.max_revenue !== "") params.set("max_revenue", filters.max_revenue);
    if (filters.min_nights !== "") params.set("min_nights", filters.min_nights);
    if (filters.max_nights !== "") params.set("max_nights", filters.max_nights);
    if (filters.country) params.set("country", filters.country.toUpperCase());
    if (filters.q) params.set("q", filters.q);
    params.set("sort_by", sortBy);
    params.set("sort_order", sortOrder);
    try {
      const { data } = await api.get(`/reservations?${params.toString()}`);
      // Post-filter for multi-select unit/property (backend only supports single)
      let filtered = data;
      if (activePropertyId) filtered = filtered.filter((r) => r.property_id === activePropertyId);
      else if (filters.property_ids.length > 1) filtered = filtered.filter((r) => filters.property_ids.includes(r.property_id));
      if (filters.unit_ids.length > 1) filtered = filtered.filter((r) => filters.unit_ids.includes(r.unit_id));
      setItems(filtered);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [u, p] = await Promise.all([api.get("/units", { params: { include_inactive: true } }), api.get("/properties")]);
        setUnits(u.data);
        setProperties(p.data);
      } catch (_e) { /* ignore lookup failure — page still works */ }
    })();
  }, []);

  useEffect(() => { loadReservations(); }, [sortBy, sortOrder, activePropertyId]);

  const unitMap = useMemo(() => Object.fromEntries(units.map((u) => [u.id, u])), [units]);
  const propMap = useMemo(() => Object.fromEntries(properties.map((p) => [p.id, p])), [properties]);

  // Source options derived from data
  const sourceOptions = useMemo(() => {
    const s = new Set(items.map((r) => r.source).filter(Boolean));
    Object.keys(CAL_SOURCE_LABELS).forEach((k) => s.add(k));
    // Dedupe by display label (e.g. `booking` + `booking_com` both map to "Booking.com").
    const seenLabels = new Set();
    return Array.from(s).filter((k) => {
      const label = CAL_SOURCE_LABELS[k] || k;
      if (seenLabels.has(label)) return false;
      seenLabels.add(label);
      return true;
    });
  }, [items]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.date_from || filters.date_to) n++;
    if (filters.arrivals_today) n++;
    if (filters.departures_today) n++;
    if (filters.status) n++;
    if (filters.unit_ids.length) n++;
    if (filters.property_ids.length) n++;
    if (filters.source.length) n++;
    if (filters.min_revenue !== "" || filters.max_revenue !== "") n++;
    if (filters.min_nights !== "" || filters.max_nights !== "") n++;
    if (filters.country) n++;
    if (filters.q) n++;
    return n;
  }, [filters]);

  const clearFilters = () => {
    setFilters({ date_from: "", date_to: "", arrivals_today: false, departures_today: false, status: "", unit_ids: [], property_ids: [], source: [], min_revenue: "", max_revenue: "", min_nights: "", max_nights: "", country: "", q: "" });
    setTimeout(loadReservations, 0);
  };

  const toggleMulti = (key, value) => {
    setFilters((f) => {
      const cur = new Set(f[key]);
      if (cur.has(value)) cur.delete(value); else cur.add(value);
      return { ...f, [key]: Array.from(cur) };
    });
  };

  // Totals for footer / print
  const totals = useMemo(() => {
    const nights = items.reduce((s, r) => s + (r.nights || 0), 0);
    const amount = items.reduce((s, r) => s + (r.total_amount || 0), 0);
    return { nights, amount };
  }, [items]);

  const openReservation = (r) => { setDrawer({ reservation: r }); setOpenId(r.id); };
  // Deep link (notifications): /reservations?open=<id>
  const [urlParams, setUrlParams] = useSearchParams();
  useEffect(() => {
    const rid = urlParams.get("open");
    if (!rid) return;
    api.get(`/reservations/${rid}`).then(({ data }) => openReservation(data)).catch(() => toast.error("Rezervacija nije pronađena"));
    const next = new URLSearchParams(urlParams); next.delete("open"); setUrlParams(next, { replace: true });
  }, [urlParams]); // eslint-disable-line react-hooks/exhaustive-deps
  const onSavedFromDrawer = (updated) => {
    setItems((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
  };
  const onDeletedFromDrawer = (id) => {
    setItems((prev) => prev.filter((r) => r.id !== id));
    setDrawer(null); setOpenId(null);
  };

  const printPdf = () => window.print();

  return (
    <div className="reservations-page space-y-4" data-testid="reservations-page">
      <div className="space-y-3 print:hidden">
        <ListHeader
          title="Rezervacije"
          summary={<>{items.length} rezervacija · {totals.nights} noći · <span className="font-semibold text-foreground">€{totals.amount.toFixed(2)}</span></>}
        />
        <ListToolbar
          value={filters.q}
          onChange={(v) => setFilters((f) => ({ ...f, q: v }))}
          onSubmit={loadReservations}
          placeholder="Traži gosta, kod, email..."
          filtersOpen={showFilters}
          onToggleFilters={() => setShowFilters((v) => !v)}
          filterCount={activeFilterCount}
          testIdPrefix="reservations"
        >
          <div className="relative flex h-10 shrink-0 items-center rounded-md border border-border bg-card">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="h-full w-[44px] appearance-none rounded-l-md bg-transparent pl-3 text-sm text-transparent focus:outline-none sm:w-auto sm:pr-2 sm:text-foreground"
              title="Sortiraj"
              data-testid="reservations-sort-by"
            >
              {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value} className="text-foreground">{o.label}</option>)}
            </select>
            <SortAscending size={16} className="pointer-events-none absolute left-3 text-muted-foreground sm:hidden" />
            <button
              onClick={() => setSortOrder((o) => o === "asc" ? "desc" : "asc")}
              className="flex h-full items-center border-l border-border px-2 text-muted-foreground hover:bg-muted"
              title={sortOrder === "asc" ? "Rastuće" : "Silazno"}
              data-testid="reservations-sort-order"
            >{sortOrder === "asc" ? <CaretUp size={14} /> : <CaretDown size={14} />}</button>
          </div>
          <ToolbarButton icon={Printer} label="PDF" onClick={printPdf} testId="reservations-print-btn" />
        </ListToolbar>

        <FilterPanel
          open={showFilters}
          onReset={clearFilters}
          onApply={loadReservations}
          canReset={activeFilterCount > 0}
          testIdPrefix="reservations"
        >
          <FilterDate value={filters.date_from} onChange={(v) => setFilters((f) => ({ ...f, date_from: v, date_to: f.date_to && f.date_to < v ? v : f.date_to }))} placeholder="Od datuma" testId="filter-date-from" />
          <FilterDate value={filters.date_to} onChange={(v) => setFilters((f) => ({ ...f, date_to: v }))} minDate={filters.date_from || undefined} placeholder="Do datuma" testId="filter-date-to" />
          <ChipToggle active={filters.arrivals_today} onClick={() => setFilters((f) => ({ ...f, arrivals_today: !f.arrivals_today }))} testId="filter-arrivals-today">Dolasci danas</ChipToggle>
          <ChipToggle active={filters.departures_today} onClick={() => setFilters((f) => ({ ...f, departures_today: !f.departures_today }))} testId="filter-departures-today">Odlasci danas</ChipToggle>
          <FilterSelect
            value={filters.status}
            onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
            options={[{ value: "", label: "Svi statusi" }, ...Object.entries(CAL_STATUS_LABELS).map(([k, v]) => ({ value: k, label: v }))]}
            testId="filter-status"
          />
          <MultiSelectDropdown
            label="Prodajni kanal"
            options={sourceOptions.map((src) => ({ id: src, label: CAL_SOURCE_LABELS[src] || src }))}
            selectedIds={filters.source}
            onToggle={(id) => toggleMulti("source", id)}
            testId="filter-source"
          />
          <MultiSelectDropdown
            label="Smještaj"
            options={properties.map((p) => ({ id: p.id, label: p.name }))}
            selectedIds={filters.property_ids}
            onToggle={(id) => toggleMulti("property_ids", id)}
            testId="filter-property"
          />
          <MultiSelectDropdown
            label="Jedinice"
            options={units.filter((u) => u.active !== false && (!filters.property_ids.length || filters.property_ids.includes(u.property_id))).map((u) => ({ id: u.id, label: u.code }))}
            selectedIds={filters.unit_ids}
            onToggle={(id) => toggleMulti("unit_ids", id)}
            testId="filter-unit"
          />
          <RangePair label="€" min={filters.min_revenue} max={filters.max_revenue} onMin={(v) => setFilters((f) => ({ ...f, min_revenue: v }))} onMax={(v) => setFilters((f) => ({ ...f, max_revenue: v }))} testId="filter-revenue" />
          <RangePair label="Noći" min={filters.min_nights} max={filters.max_nights} onMin={(v) => setFilters((f) => ({ ...f, min_nights: v }))} onMax={(v) => setFilters((f) => ({ ...f, max_nights: v }))} testId="filter-nights" />
          <input type="text" maxLength="2" placeholder="Država (npr. HR)" value={filters.country} onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value.toUpperCase() }))} className="rw-control uppercase placeholder:normal-case" data-testid="filter-country" />
        </FilterPanel>

        {selected.size > 0 && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm dark:border-rose-900/50 dark:bg-rose-950/30">
            <span className="text-rose-700 dark:text-rose-300">{selected.size} odabrano</span>
            <button onClick={deleteSelected} className="inline-flex h-8 items-center gap-1.5 rounded-md bg-rose-600 px-3 text-xs font-semibold text-white hover:bg-rose-700" data-testid="res-bulk-delete">
              <Trash size={13} weight="bold" /> Obriši
            </button>
          </div>
        )}
      </div>

      <div>
        {/* Main table */}
        <div className="space-y-2">
          {/* Print-only document header (tenant-branded) — hidden on screen */}
          <div className="rw-print-header">
            <h1>Popis rezervacija — {tenant?.brand_name || tenant?.name || "PMS"}</h1>
            <p>{new Date().toLocaleString("hr-HR")}</p>
          </div>
          {/* Print-only meta line summarizing filters & totals — hidden on screen */}
          <div className="rw-print-meta">
            <div className="rw-print-meta-summary">
              <strong>Rezervacija:</strong> {items.length} · <strong>Ukupno noći:</strong> {totals.nights} · <strong>Iznos:</strong> €{totals.amount.toFixed(2)}
            </div>
            {(filters.date_from || filters.date_to || filters.arrivals_today || filters.departures_today || filters.check_in_from || filters.check_in_to || filters.status || filters.source?.length || filters.country || filters.q || filters.unit_ids?.length || filters.property_ids?.length || activePropertyId) && (
              <div className="rw-print-meta-filters">
                <strong>Primijenjeni filteri:</strong>
                <ul>
                  {activePropertyId && <li>Aktivan smještaj: <em>{properties.find((p) => p.id === activePropertyId)?.name || activePropertyId}</em></li>}
                  {(filters.date_from || filters.date_to) && <li>Period: {filters.date_from || "*"} → {filters.date_to || "*"}</li>}
                  {(filters.check_in_from || filters.check_in_to) && <li>Prijava: {filters.check_in_from || "*"} → {filters.check_in_to || "*"}</li>}
                  {filters.arrivals_today && <li>Dolasci danas</li>}
                  {filters.departures_today && <li>Odlasci danas</li>}
                  {filters.status && <li>Status: {CAL_STATUS_LABELS[filters.status] || filters.status}</li>}
                  {filters.source?.length ? <li>Izvori: {filters.source.map((s) => CAL_SOURCE_LABELS[s] || s).join(", ")}</li> : null}
                  {filters.country && <li>Država: {filters.country}</li>}
                  {filters.q && <li>Pretraga: „{filters.q}“</li>}
                  {filters.unit_ids?.length ? <li>Jedinice: {filters.unit_ids.length}</li> : null}
                  {filters.property_ids?.length > 1 ? <li>Objekti: {filters.property_ids.length}</li> : null}
                </ul>
              </div>
            )}
          </div>
          {loading ? (
            <div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-primary" /></div>
          ) : items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-10 text-center">
              <Receipt size={36} className="mx-auto text-muted-foreground" />
              <p className="mt-3 text-sm font-medium text-foreground">Nema rezervacija</p>
              <p className="mt-1 text-xs text-muted-foreground">Podesi filtere ili kreiraj rezervaciju iz Kalendara.</p>
            </div>
          ) : (
            <>
              {/* Desktop/tablet table view */}
              <div className="hidden overflow-x-auto rounded-lg border border-border bg-card md:block">
              <table className="w-full text-sm" data-testid="reservations-table">
                <thead className="bg-muted/40 text-xs uppercase text-muted-foreground print:bg-white">
                  <tr>
                    <th className="w-8 px-2 py-2 text-center print:hidden">
                      <input
                        type="checkbox"
                        checked={items.length > 0 && selected.size === items.length}
                        onChange={(e) => toggleAll(e.target.checked)}
                        className="h-4 w-4"
                        data-testid="res-select-all"
                      />
                    </th>
                    <th className="w-8 px-2 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left font-semibold">Rezervirano</th>
                    <th className="px-3 py-2 text-left font-semibold">Gost</th>
                    <th className="px-3 py-2 text-left font-semibold">Period</th>
                    <th className="px-3 py-2 text-right font-semibold">Iznos</th>
                    <th className="px-3 py-2 text-left font-semibold">Jedinica</th>
                    <th className="hidden px-3 py-2 text-left font-semibold print:table-cell">Napomena</th>
                    <th className="px-3 py-2 text-left font-semibold print:hidden">Status</th>
                    <th className="px-3 py-2 text-center font-semibold print:hidden">Akcije</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((r, idx) => {
                    // In PRINT mode we always show the ORIGINAL unit that the
                    // guest booked (snapshot at reservation time), even if the
                    // reservation was later moved in the calendar.
                    const originalUnit = unitMap[r.original_unit_id] || unitMap[r.unit_id];
                    const u = unitMap[r.unit_id];
                    const p = propMap[r.property_id];
                    const s = STATUS_BADGE[r.status] || STATUS_BADGE.confirmed;
                    return (
                      <tr
                        key={r.id}
                        className={`cursor-pointer transition ${openId === r.id ? "bg-primary/5" : selected.has(r.id) ? "bg-primary/10" : "hover:bg-muted/30"}`}
                        onClick={() => openReservation(r)}
                        data-testid={`res-row-${r.code}`}
                      >
                        <td className="px-2 py-2 text-center print:hidden" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected.has(r.id)}
                            onChange={() => toggleSel(r.id)}
                            className="h-4 w-4"
                            data-testid={`res-select-${r.code}`}
                          />
                        </td>
                        <td className="px-2 py-2 text-center">
                          <span className="print:hidden inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: reservationColor(r.source, tenant?.source_colors) }} title={CAL_SOURCE_LABELS[r.source] || r.source} />
                          <span className="hidden font-mono text-[10px] print:inline">{idx + 1}</span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-xs text-foreground">{fmtDate(r.created_at)}</div>
                          <div className="text-[10px] text-muted-foreground">{fmtDateTime(r.created_at).split(" ")[1]}</div>
                        </td>
                        <td className="px-3 py-2 rw-cell-guest">
                          <div className="font-medium text-foreground underline-offset-2 hover:underline">{r.primary_guest_name}</div>
                          <div className="text-[10px] text-muted-foreground">{r.adults} od{r.children ? `, ${r.children} dj` : ", 0 dj"}</div>
                        </td>
                        <td className="px-3 py-2 rw-cell-period">
                          <div className="text-xs text-foreground">{fmtDate(r.check_in)} - {fmtDate(r.check_out)}</div>
                          <div className="text-[10px] text-muted-foreground">{r.nights} noćenja</div>
                        </td>
                        <td className="px-3 py-2 text-right rw-cell-amount">
                          <div className="font-mono text-xs font-semibold text-foreground whitespace-nowrap">{(r.total_amount || 0).toFixed(2)} EUR</div>
                          <div className="text-[10px] text-muted-foreground whitespace-nowrap">{r.nights > 0 ? ((r.total_amount || 0) / r.nights).toFixed(2) : "0.00"} EUR / noć</div>
                        </td>
                        <td className="px-3 py-2 rw-cell-unit">
                          {/* On screen: current unit. In print: original booked unit (snapshot). */}
                          <div className="text-xs text-foreground">
                            <span className="print:hidden">{u?.code || "—"}</span>
                            <span className="hidden print:inline">{originalUnit?.code || u?.code || "—"}</span>
                          </div>
                          <div className="text-[10px] text-muted-foreground">{CAL_SOURCE_LABELS[r.source] || r.source}</div>
                        </td>
                        <td className="hidden px-3 py-2 max-w-xs rw-cell-notes print:table-cell">
                          {/* On screen: truncated with tooltip. In print: full wrapped text. */}
                          <div className="truncate text-[11px] text-muted-foreground print:hidden" title={r.notes || ""}>{r.notes || "—"}</div>
                          <div className="hidden print:block text-[10px] text-muted-foreground whitespace-pre-wrap break-words">{r.notes || "—"}</div>
                        </td>
                        <td className="px-3 py-2 print:hidden">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${s.bg} ${s.text}`}>
                            {CAL_STATUS_LABELS[r.status] || r.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center print:hidden" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => deleteReservation(r.id)}
                            className="rounded p-1.5 text-muted-foreground hover:bg-rose-50 hover:text-rose-600"
                            title="Obriši rezervaciju"
                            data-testid={`res-delete-${r.code}`}
                          >
                            <Trash size={14} weight="bold" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="hidden print:table-footer-group">
                  <tr className="border-t-2 border-border">
                    <td colSpan="4" className="px-3 py-2 text-right text-xs font-semibold">Ukupno {items.length} rezervacija, {totals.nights} noćenja</td>
                    <td className="px-3 py-2 text-right font-mono text-xs font-semibold">€{totals.amount.toFixed(2)}</td>
                    <td colSpan="3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Mobile card view */}
            <div className="space-y-2 md:hidden" data-testid="reservations-mobile-cards">
              {items.map((r, idx) => {
                const u = unitMap[r.unit_id];
                const s = STATUS_BADGE[r.status] || STATUS_BADGE.confirmed;
                return (
                  <div
                    key={r.id}
                    onClick={() => openReservation(r)}
                    className={`cursor-pointer rounded-lg border bg-card px-3 py-3 transition active:scale-[0.99] ${openId === r.id || selected.has(r.id) ? "border-primary bg-primary/5" : "border-border"}`}
                    data-testid={`res-card-${r.code}`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onChange={() => toggleSel(r.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 shrink-0"
                        data-testid={`res-card-select-${r.code}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: reservationColor(r.source, tenant?.source_colors) }} />
                          <p className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">{r.primary_guest_name}</p>
                          <span className="shrink-0 font-mono text-sm font-semibold text-foreground">€{(r.total_amount || 0).toFixed(2)}</span>
                        </div>
                        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="min-w-0 flex-1 truncate">{fmtShortDate(r.check_in)} – {fmtShortDate(r.check_out)} · {r.nights} {r.nights === 1 ? "noć" : "noći"} · {u?.code || "—"}</span>
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${s.bg} ${s.text}`}>{CAL_STATUS_LABELS[r.status] || r.status}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            </>
          )}
        </div>

      </div>

      {drawer && (
        <ReservationDrawer
          reservation={drawer.reservation}
          units={units}
          properties={properties}
          onClose={() => { setDrawer(null); setOpenId(null); }}
          onSaved={onSavedFromDrawer}
          onDeleted={onDeletedFromDrawer}
        />
      )}

      <style>{`
        .rw-filter-input { width:100%; border:1px solid hsl(var(--border)); background:hsl(var(--background)); padding:0.5rem 0.65rem; font-size:0.875rem; border-radius:0.4rem; color:hsl(var(--foreground)); }
        .rw-filter-input:focus { outline:none; border-color:hsl(var(--primary)); box-shadow:0 0 0 2px hsl(var(--primary) / 0.2); }

        @media print {
          /* margin:0 makes Chrome/Firefox suppress their auto-generated
             page headers (date/title/URL) and footers. We compensate with
             padding inside the printable root. */
          @page { size: A4 portrait; margin: 0; }
          @page { @bottom-right { content: counter(page) " / " counter(pages); font-size: 9px; color: #666; padding: 4mm; } }

          html, body { background:#fff !important; margin: 0 !important; padding: 0 !important; }

          /* Hide the app chrome — sidebar, top bar, drawer, toolbar, filter panel */
          [data-testid="app-sidebar"],
          [data-testid="mobile-sidebar"],
          .app-sidebar,
          header,
          nav,
          aside.sidebar,
          [data-testid="reservation-drawer"],
          [data-testid="reservation-drawer-overlay"],
          .print\\:hidden { display: none !important; }
          [data-testid="app-main"] { padding: 0 !important; margin: 0 !important; }

          /* Keep only the reservations-page content visible and full-width */
          .reservations-page {
            position: static !important;
            margin: 0 !important;
            padding: 12mm 12mm 18mm 12mm !important;
            max-width: 100% !important;
            width: 100% !important;
          }
          .reservations-page table { font-size: 10px; width: 100% !important; table-layout: auto !important; }
          .reservations-page thead { background: #f5f5f5 !important; }
          .reservations-page thead th { border-bottom: 1.5px solid #333 !important; padding: 6px 8px !important; }
          .reservations-page tbody td { padding: 6px 8px !important; vertical-align: top !important; }
          .reservations-page tbody tr { page-break-inside: avoid; }
          /* Column-specific widths — prevents "Guest name" / "iznos" wrapping into pancake shape */
          .rw-cell-guest { min-width: 26mm; white-space: normal !important; }
          .rw-cell-period { min-width: 30mm; white-space: nowrap !important; }
          .rw-cell-amount { min-width: 25mm; white-space: nowrap !important; }
          .rw-cell-unit { min-width: 22mm; }
          .rw-cell-notes { width: 55mm; }
          .rw-cell-notes .whitespace-pre-wrap { max-width: 55mm; }

          /* Tenant-branded document header */
          .rw-print-header {
            display: block !important;
            padding-bottom: 3mm;
            margin-bottom: 4mm;
            border-bottom: 2px solid #001A38;
          }
          .rw-print-header h1 {
            font-weight: 700 !important;
            font-size: 16px !important;
            color: #111 !important;
            margin: 0 0 1mm 0 !important;
          }
          .rw-print-header p {
            font-size: 10px !important;
            color: #555 !important;
            margin: 0 !important;
          }

          .rw-print-meta {
            display: block !important;
            font-size: 10px;
            color: #333;
            margin-bottom: 6mm;
          }
          .rw-print-meta-summary { margin-bottom: 2mm; }
          .rw-print-meta-filters { padding: 3mm 4mm; background: #f8f9fb; border-left: 3px solid #001A38; }
          .rw-print-meta-filters strong { display: block; margin-bottom: 1mm; font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; color: #555; }
          .rw-print-meta-filters ul { margin: 0; padding-left: 4mm; list-style: disc; }
          .rw-print-meta-filters li { margin-bottom: 0.5mm; }
        }
        /* Hidden on screen, visible on print */
        .rw-print-meta, .rw-print-header { display: none; }
      `}</style>
    </div>
  );
}

function ChipToggle({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex h-10 min-w-0 items-center justify-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors ${active ? "border-primary bg-primary/10 text-primary" : "border-border bg-background text-muted-foreground hover:text-foreground"}`}
      aria-pressed={active}
      data-testid={testId}
    >
      {active && <Check size={13} weight="bold" className="shrink-0" />}
      <span className="truncate">{children}</span>
    </button>
  );
}

function RangePair({ label, min, max, onMin, onMax, testId }) {
  return (
    <div className="flex h-10 min-w-0 items-center overflow-hidden rounded-md border border-border bg-background focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
      <span className="shrink-0 border-r border-border px-2.5 text-xs font-semibold text-muted-foreground">{label}</span>
      <input type="number" inputMode="numeric" placeholder="od" value={min} onChange={(e) => onMin(e.target.value)} className="h-full w-full min-w-0 bg-transparent px-2 text-sm focus:outline-none" data-testid={`${testId.replace("filter-", "filter-min-")}`} />
      <span className="text-muted-foreground">–</span>
      <input type="number" inputMode="numeric" placeholder="do" value={max} onChange={(e) => onMax(e.target.value)} className="h-full w-full min-w-0 bg-transparent px-2 text-sm focus:outline-none" data-testid={`${testId.replace("filter-", "filter-max-")}`} />
    </div>
  );
}

/** Filter control that opens a checkbox popover. Click-outside closes. */
function MultiSelectDropdown({ label, options, selectedIds, onToggle, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);
  const count = selectedIds.length;
  return (
    <div className="relative min-w-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex h-10 w-full min-w-0 items-center justify-between gap-2 rounded-md border px-3 text-sm transition-colors ${count ? "border-primary bg-primary/10 text-primary" : "border-border bg-background text-foreground hover:border-primary/50"}`}
        data-testid={`${testId}-toggle`}
      >
        <span className="truncate">{label}</span>
        <span className="flex shrink-0 items-center gap-1.5">
          {count > 0 && <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold leading-none text-primary-foreground">{count}</span>}
          <CaretDown size={12} weight="bold" className="opacity-60" />
        </span>
      </button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 max-h-64 w-full min-w-[200px] overflow-y-auto rounded-md border border-border bg-popover p-1.5 shadow-lg" data-testid={`${testId}-menu`}>
          {options.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">Nema opcija</p>
          ) : options.map((o) => (
            <label key={o.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm hover:bg-muted">
              <input type="checkbox" className="h-4 w-4 shrink-0" checked={selectedIds.includes(o.id)} onChange={() => onToggle(o.id)} data-testid={`${testId}-${o.id}`} />
              <span className="truncate">{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
